import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Shield, Camera, CreditCard, IdCard, Check, X, 
  Sparkles, Users, Calendar, MapPin, Clock, ChevronRight,
  AlertTriangle, Lock, Star, Percent, ThumbsUp, ThumbsDown,
  User, Settings, Eye, EyeOff, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined options for likes/dislikes
const INTEREST_OPTIONS = [
  'Travel', 'Music', 'Movies', 'Reading', 'Cooking', 'Fitness', 'Gaming', 
  'Art', 'Photography', 'Dancing', 'Hiking', 'Yoga', 'Sports', 'Fashion',
  'Technology', 'Nature', 'Animals', 'Food', 'Wine', 'Coffee'
];

const PERSONALITY_TRAITS = [
  'Adventurous', 'Ambitious', 'Creative', 'Empathetic', 'Funny', 'Honest',
  'Intellectual', 'Kind', 'Optimistic', 'Patient', 'Romantic', 'Spontaneous'
];

const LOOKING_FOR_OPTIONS = [
  { id: 'relationship', label: 'Serious Relationship', icon: Heart },
  { id: 'casual', label: 'Casual Dating', icon: Users },
  { id: 'friendship', label: 'New Friends', icon: Star },
  { id: 'events', label: 'Singles Events', icon: Calendar }
];

export default function DatingMingle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState('check'); // check, upgrade, verify-id, verify-card, verify-selfie, profile, browse
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('discover'); // discover, matches, events, profile
  
  // Verification form states
  const [idForm, setIdForm] = useState({
    id_type: 'drivers_license',
    id_number: '',
    full_name: '',
    date_of_birth: '',
    id_image_url: ''
  });
  const [cardToken, setCardToken] = useState('');
  const [selfieUrl, setSelfieUrl] = useState('');
  
  // Profile form states
  const [profileForm, setProfileForm] = useState({
    bio: '',
    gender: '',
    interested_in: [],
    looking_for: [],
    interests: [],
    likes: [],
    dislikes: [],
    personality_traits: [],
    deal_breakers: [],
    age_range_min: 18,
    age_range_max: 50,
    photos: []
  });
  
  const [newLike, setNewLike] = useState('');
  const [newDislike, setNewDislike] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token') || localStorage.getItem('socyy_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  // Check access status on mount
  useEffect(() => {
    checkAccessStatus();
  }, []);

  const checkAccessStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dating/access-status`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setAccessStatus(data);
      
      if (!data.has_subscription) {
        setCurrentStep('upgrade');
      } else if (!data.verification?.id_verified) {
        setCurrentStep('verify-id');
      } else if (!data.verification?.card_verified) {
        setCurrentStep('verify-card');
      } else if (!data.verification?.selfie_verified) {
        setCurrentStep('verify-selfie');
      } else if (data.can_access) {
        setCurrentStep('browse');
        loadDatingData();
      }
    } catch (error) {
      toast.error('Failed to check access status');
    } finally {
      setLoading(false);
    }
  };

  const loadDatingData = async () => {
    try {
      const [profileRes, discoverRes, matchesRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/dating/profile`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/dating/discover?sort_by=compatibility`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/dating/matches`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/dating/events`, { headers: getAuthHeaders() })
      ]);
      
      const [profileData, discoverData, matchesData, eventsData] = await Promise.all([
        profileRes.json(),
        discoverRes.json(),
        matchesRes.json(),
        eventsRes.json()
      ]);
      
      if (profileData.has_profile) {
        setProfile(profileData.profile);
        setProfileForm({
          ...profileForm,
          ...profileData.profile
        });
      } else {
        setActiveTab('profile');
      }
      
      setProfiles(discoverData.profiles || []);
      setMatches(matchesData.matches || []);
      setEvents(eventsData.events || []);
    } catch (error) {
      console.error('Error loading dating data:', error);
    }
  };

  const handleIdVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dating/verify/id`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(idForm)
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success('ID verified successfully!');
        setCurrentStep('verify-card');
        checkAccessStatus();
      } else {
        toast.error(data.detail || 'Verification failed');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCardVerification = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dating/verify/card`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ card_token: cardToken || 'sim_token' })
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Card verified! $1 charged and refunded.');
        setCurrentStep('verify-selfie');
        checkAccessStatus();
      } else {
        toast.error(data.detail || 'Verification failed');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelfieVerification = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dating/verify/selfie`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ selfie_image_url: selfieUrl || 'selfie_verified' })
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Verification complete! Welcome to Dating Mingle.');
        setCurrentStep('browse');
        checkAccessStatus();
      } else {
        toast.error(data.detail || 'Verification failed');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dating/profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileForm)
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Profile saved!');
        setProfile(data.profile);
        setActiveTab('discover');
        loadDatingData();
      } else {
        toast.error(data.detail || 'Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const addCustomItem = (field, value, setter) => {
    if (value.trim() && !profileForm[field].includes(value.trim())) {
      setProfileForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeItem = (field, item) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].filter(i => i !== item)
    }));
  };

  const toggleArrayItem = (field, item) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  // Render upgrade prompt
  const renderUpgradePrompt = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-lg mx-auto text-center p-8"
    >
      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
        <Lock className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold mb-4">Dating Mingle Requires Premium</h2>
      <p className="text-gray-400 mb-6">
        Dating Mingle is an exclusive feature for Verified Premium subscribers and above. 
        Upgrade your account to access singles matching, events, and more.
      </p>
      <button
        onClick={() => navigate('/pricing')}
        className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full font-semibold hover:opacity-90 transition"
      >
        Upgrade to Premium
      </button>
    </motion.div>
  );

  // Render verification steps
  const renderVerificationStep = () => {
    const steps = [
      { id: 'verify-id', label: 'ID Verification', icon: IdCard, done: accessStatus?.verification?.id_verified },
      { id: 'verify-card', label: 'Card Verification', icon: CreditCard, done: accessStatus?.verification?.card_verified },
      { id: 'verify-selfie', label: 'Selfie Verification', icon: Camera, done: accessStatus?.verification?.selfie_verified }
    ];

    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto p-6"
      >
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {steps.map((step, idx) => (
            <React.Fragment key={step.id}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                step.done ? 'bg-green-500/20 text-green-400' :
                currentStep === step.id ? 'bg-pink-500/20 text-pink-400' :
                'bg-gray-800 text-gray-500'
              }`}>
                {step.done ? <Check className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                <span className="text-sm font-medium">{step.label}</span>
              </div>
              {idx < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-600" />}
            </React.Fragment>
          ))}
        </div>

        {/* ID Verification Form */}
        {currentStep === 'verify-id' && (
          <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-800">
            <div className="text-center mb-6">
              <IdCard className="w-12 h-12 mx-auto mb-4 text-pink-400" />
              <h3 className="text-xl font-bold">Verify Your Identity</h3>
              <p className="text-gray-400 text-sm mt-2">
                We need to verify you're a real person to keep our community safe.
              </p>
            </div>
            
            <form onSubmit={handleIdVerification} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">ID Type</label>
                <select
                  value={idForm.id_type}
                  onChange={(e) => setIdForm({ ...idForm, id_type: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                >
                  <option value="drivers_license">Driver's License</option>
                  <option value="passport">Passport</option>
                  <option value="national_id">National ID</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Full Name (as on ID)</label>
                <input
                  type="text"
                  value={idForm.full_name}
                  onChange={(e) => setIdForm({ ...idForm, full_name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={idForm.date_of_birth}
                  onChange={(e) => setIdForm({ ...idForm, date_of_birth: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">ID Number</label>
                <input
                  type="text"
                  value={idForm.id_number}
                  onChange={(e) => setIdForm({ ...idForm, id_number: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  placeholder="ID Number"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Upload ID Photo</label>
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-pink-500 transition cursor-pointer">
                  <Camera className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                  <p className="text-sm text-gray-400">Click to upload or drag and drop</p>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => setIdForm({ ...idForm, id_image_url: e.target.files[0]?.name || 'uploaded' })}
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-600 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify ID'}
              </button>
            </form>
          </div>
        )}

        {/* Card Verification */}
        {currentStep === 'verify-card' && (
          <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-800">
            <div className="text-center mb-6">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-pink-400" />
              <h3 className="text-xl font-bold">Card Verification</h3>
              <p className="text-gray-400 text-sm mt-2">
                We'll charge $1 to verify your card and immediately refund it.
              </p>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3 text-yellow-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">Your card will be charged $1.00 and refunded instantly.</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Card Number</label>
                <input
                  type="text"
                  placeholder="4242 4242 4242 4242"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  onChange={(e) => setCardToken(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Expiry</label>
                  <input
                    type="text"
                    placeholder="MM/YY"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CVC</label>
                  <input
                    type="text"
                    placeholder="123"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
                  />
                </div>
              </div>
              
              <button
                onClick={handleCardVerification}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-600 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Verify Card ($1 Hold)'}
              </button>
            </div>
          </div>
        )}

        {/* Selfie Verification */}
        {currentStep === 'verify-selfie' && (
          <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-800">
            <div className="text-center mb-6">
              <Camera className="w-12 h-12 mx-auto mb-4 text-pink-400" />
              <h3 className="text-xl font-bold">Selfie Verification</h3>
              <p className="text-gray-400 text-sm mt-2">
                Take a selfie to match with your ID photo.
              </p>
            </div>
            
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center hover:border-pink-500 transition cursor-pointer mb-6">
              <Camera className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">Click to take a selfie or upload</p>
              <input
                type="file"
                accept="image/*"
                capture="user"
                className="sr-only"
                onChange={(e) => setSelfieUrl(e.target.files[0]?.name || 'selfie')}
              />
            </div>
            
            <button
              onClick={handleSelfieVerification}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-600 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Complete Verification'}
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  // Render profile form
  const renderProfileForm = () => (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="dating-profile-form">
      <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold mb-4">Basic Info</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Bio</label>
            <textarea
              value={profileForm.bio}
              onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 min-h-[100px]"
              placeholder="Tell potential matches about yourself..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">I am</label>
              <select
                value={profileForm.gender}
                onChange={(e) => setProfileForm({ ...profileForm, gender: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3"
              >
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Interested in</label>
              <div className="flex flex-wrap gap-2">
                {['male', 'female', 'non-binary', 'everyone'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleArrayItem('interested_in', opt)}
                    className={`px-3 py-1.5 rounded-full text-sm capitalize ${
                      profileForm.interested_in.includes(opt)
                        ? 'bg-pink-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Age Range Preference</label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="18"
                max="99"
                value={profileForm.age_range_min}
                onChange={(e) => setProfileForm({ ...profileForm, age_range_min: parseInt(e.target.value) })}
                className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2"
              />
              <span className="text-gray-500">to</span>
              <input
                type="number"
                min="18"
                max="99"
                value={profileForm.age_range_max}
                onChange={(e) => setProfileForm({ ...profileForm, age_range_max: parseInt(e.target.value) })}
                className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Looking For */}
      <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold mb-4">What are you looking for?</h3>
        <div className="grid grid-cols-2 gap-3">
          {LOOKING_FOR_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggleArrayItem('looking_for', opt.id)}
              className={`flex items-center gap-3 p-4 rounded-xl border transition ${
                profileForm.looking_for.includes(opt.id)
                  ? 'border-pink-500 bg-pink-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <opt.icon className={`w-5 h-5 ${profileForm.looking_for.includes(opt.id) ? 'text-pink-400' : 'text-gray-500'}`} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Likes & Dislikes */}
      <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Percent className="w-5 h-5 text-pink-400" />
          Compatibility Preferences
        </h3>
        
        {/* Likes */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3 flex items-center gap-2">
            <ThumbsUp className="w-4 h-4 text-green-400" />
            Things I Like
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {INTEREST_OPTIONS.map(item => (
              <button
                key={item}
                type="button"
                onClick={() => toggleArrayItem('likes', item)}
                className={`px-3 py-1.5 rounded-full text-sm transition ${
                  profileForm.likes.includes(item)
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newLike}
              onChange={(e) => setNewLike(e.target.value)}
              placeholder="Add custom..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomItem('likes', newLike, setNewLike))}
            />
            <button
              type="button"
              onClick={() => addCustomItem('likes', newLike, setNewLike)}
              className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Dislikes */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3 flex items-center gap-2">
            <ThumbsDown className="w-4 h-4 text-red-400" />
            Things I Dislike
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {profileForm.dislikes.map(item => (
              <span
                key={item}
                className="px-3 py-1.5 rounded-full text-sm bg-red-500/20 text-red-400 border border-red-500/50 flex items-center gap-2"
              >
                {item}
                <button type="button" onClick={() => removeItem('dislikes', item)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newDislike}
              onChange={(e) => setNewDislike(e.target.value)}
              placeholder="Add dislike..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomItem('dislikes', newDislike, setNewDislike))}
            />
            <button
              type="button"
              onClick={() => addCustomItem('dislikes', newDislike, setNewDislike)}
              className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Personality Traits */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3">My Personality</label>
          <div className="flex flex-wrap gap-2">
            {PERSONALITY_TRAITS.map(trait => (
              <button
                key={trait}
                type="button"
                onClick={() => toggleArrayItem('personality_traits', trait)}
                className={`px-3 py-1.5 rounded-full text-sm transition ${
                  profileForm.personality_traits.includes(trait)
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {trait}
              </button>
            ))}
          </div>
        </div>

        {/* Deal Breakers */}
        <div>
          <label className="block text-sm font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            Deal Breakers
          </label>
          <p className="text-xs text-gray-500 mb-3">People with these traits won't appear in your matches</p>
          <div className="flex flex-wrap gap-2">
            {['Smoking', 'Heavy Drinking', 'No Kids', 'Has Kids', 'Long Distance', 'Different Religion'].map(item => (
              <button
                key={item}
                type="button"
                onClick={() => toggleArrayItem('deal_breakers', item)}
                className={`px-3 py-1.5 rounded-full text-sm transition ${
                  profileForm.deal_breakers.includes(item)
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={handleSaveProfile}
        disabled={loading}
        className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
      >
        {loading ? 'Saving...' : 'Save Profile'}
      </button>
    </div>
  );

  // Render discover tab
  const renderDiscover = () => (
    <div className="space-y-4" data-testid="dating-discover">
      {profiles.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Matches Found</h3>
          <p className="text-gray-400">Try adjusting your preferences or check back later</p>
        </div>
      ) : (
        profiles.map(p => (
          <motion.div
            key={p.user_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800 hover:border-pink-500/50 transition"
          >
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-2xl font-bold">
                {p.user?.name?.[0] || '?'}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold">{p.user?.name || 'Anonymous'}</h3>
                  <span className="text-gray-400">{p.age} years</span>
                  {p.user?.is_verified && (
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Verified</span>
                  )}
                </div>
                
                {p.bio && <p className="text-gray-400 text-sm mb-3">{p.bio}</p>}
                
                {/* Compatibility Score */}
                {p.compatibility && (
                  <div className="flex items-center gap-4 mb-3">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                      p.compatibility.percentage >= 80 ? 'bg-green-500/20 text-green-400' :
                      p.compatibility.percentage >= 60 ? 'bg-blue-500/20 text-blue-400' :
                      p.compatibility.percentage >= 40 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      <Percent className="w-4 h-4" />
                      <span className="font-bold">{p.compatibility.percentage}%</span>
                      <span className="text-xs">{p.compatibility.compatibility_level}</span>
                    </div>
                  </div>
                )}
                
                {/* Shared Interests */}
                {p.compatibility?.breakdown?.interests?.shared?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {p.compatibility.breakdown.interests.shared.map(interest => (
                      <span key={interest} className="px-2 py-1 bg-pink-500/20 text-pink-400 text-xs rounded-full">
                        {interest}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Interests */}
                {p.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {p.interests.slice(0, 5).map(interest => (
                      <span key={interest} className="px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded-full">
                        {interest}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <button className="p-3 bg-pink-500/20 text-pink-400 rounded-full hover:bg-pink-500/30 transition">
                <Heart className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  // Render matches tab
  const renderMatches = () => (
    <div className="space-y-4" data-testid="dating-matches">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-yellow-400" />
        Top Matches
      </h3>
      
      {matches.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-2xl border border-gray-800">
          <Heart className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Top Matches Yet</h3>
          <p className="text-gray-400">Complete your profile to find compatible matches</p>
        </div>
      ) : (
        matches.map(m => (
          <div key={m.profile.user_id} className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-xl font-bold">
                {m.user?.name?.[0] || '?'}
              </div>
              <div className="flex-1">
                <h4 className="font-bold">{m.user?.name}</h4>
                <p className="text-sm text-gray-400">{m.profile.bio?.slice(0, 100)}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-400">{m.compatibility.percentage}%</div>
                <div className="text-xs text-gray-500">{m.compatibility.compatibility_level}</div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Render events tab
  const renderEvents = () => (
    <div className="space-y-4" data-testid="dating-events">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Calendar className="w-5 h-5 text-pink-400" />
        Singles Events
      </h3>
      
      {events.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-2xl border border-gray-800">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Upcoming Events</h3>
          <p className="text-gray-400">Check back soon for singles events in your area</p>
        </div>
      ) : (
        events.map(event => (
          <div key={event.event_id} className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800 hover:border-pink-500/50 transition">
            <div className="flex gap-4">
              {event.image_url && (
                <img src={event.image_url} alt={event.title} className="w-24 h-24 rounded-xl object-cover" />
              )}
              <div className="flex-1">
                <h4 className="font-bold text-lg mb-1">{event.title}</h4>
                <p className="text-sm text-gray-400 mb-2">{event.description}</p>
                <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.venue_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(event.start_time).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {event.attendee_count}/{event.capacity}
                  </span>
                </div>
              </div>
              <button className="self-center px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition">
                {event.user_rsvp === 'attending' ? 'Attending' : 'RSVP'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  if (loading && currentStep === 'check') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Get user data for FloatingDock
  const userData = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-950 text-white" data-testid="dating-mingle-page">
      <FloatingDock user={userData} />
      
      <main className="max-w-4xl mx-auto px-4 py-8 pb-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Dating Mingle</h1>
              <p className="text-gray-400 text-sm">Find your perfect match</p>
            </div>
          </div>
          
          {accessStatus?.verification?.fully_verified && (
            <div className="flex items-center gap-2 text-green-400 text-sm mt-2">
              <Shield className="w-4 h-4" />
              <span>Fully Verified Member</span>
            </div>
          )}
        </div>

        {/* Content based on step */}
        {currentStep === 'upgrade' && renderUpgradePrompt()}
        
        {(currentStep === 'verify-id' || currentStep === 'verify-card' || currentStep === 'verify-selfie') && renderVerificationStep()}
        
        {currentStep === 'browse' && (
          <>
            {/* Navigation Tabs */}
            <div className="flex gap-2 mb-6 bg-gray-900/50 p-1 rounded-xl w-fit">
              {[
                { id: 'discover', label: 'Discover', icon: Users },
                { id: 'matches', label: 'Top Matches', icon: Sparkles },
                { id: 'events', label: 'Events', icon: Calendar },
                { id: 'profile', label: 'My Profile', icon: User }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    activeTab === tab.id
                      ? 'bg-pink-500 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {activeTab === 'discover' && renderDiscover()}
                {activeTab === 'matches' && renderMatches()}
                {activeTab === 'events' && renderEvents()}
                {activeTab === 'profile' && renderProfileForm()}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </main>
    </div>
  );
}
